import { useMemo } from 'react';

import { useSearchCauses } from 'core/Api';
import { useBreakPoints, useUserLocation } from 'core/Hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Carousel,
  CauseDetail,
  CauseStoreCard,
  Group,
  Stack,
  Text,
  ThemeIcon,
} from 'ui';
import { IconChevronLeft, IconChevronRight, IconLocation } from 'ui/Icons';

import classes from './CausesNearYou.module.css';

const LocationExploreCard = () => {
  const { getLocation } = useUserLocation({
    showPromptNotification: false,
  });

  return (
    <Card withBorder bg="gray.0" p="md" bd={'1px solid gray.1'}>
      <Group wrap="nowrap">
        <ThemeIcon c="cobalt.3" variant="transparent" size="40px">
          <IconLocation size="40px" />
        </ThemeIcon>
        <Stack style={{ wordBreak: 'break-word' }} gap="4px">
          <Text fz="12px" fw={600} c="gray.6">
            Find orgs near you
          </Text>
          <Text fz="10px" c="gray.6">
            Turn on location to find nearby schools and non-profits faster. You can always change
            this later in your settings.
          </Text>
        </Stack>
        <Box w="140px">
          <Button size="sm" onClick={getLocation}>
            Explore Near Me
          </Button>
        </Box>
      </Group>
    </Card>
  );
};

const CausesSlides = () => {
  const navigate = useNavigate();
  const { geolocation } = useUserLocation({
    showPromptNotification: false,
  });

  const userLonLat = useMemo(() => {
    if (!geolocation) return '';
    // The API still isn't semantic, it actually searches for lat,lng instead of lon,lat
    return `${geolocation.latitude},${geolocation.longitude},25`;
  }, [geolocation]);

  const { data: causes } = useSearchCauses({
    queryOptions: {
      searches: { lonlat: { value: userLonLat }, stripe_account: { value: 'connected' } },
      join: 'AND',
    },

    pagination: {
      limit: 20,
    },
    enabled: !!userLonLat,
  });

  const { isSmallScreen } = useBreakPoints();

  const causeSlides = useMemo(
    () =>
      causes?.map((cause, index) => {
        const website = cause?.links?.find((link) => link.type == 'website')?.url;

        return (
          <Carousel.Slide key={`${cause.uuid}-${index}`}>
            <CauseStoreCard
              direction="row"
              imgSrc={cause.logo?.url}
              title={cause.name}
              info={{
                detail: <CauseDetail city={cause.city} state={cause.state} website={website} />,
              }}
              onClick={() => navigate(`/onboarding/causes/${cause.uuid}`)}
              w="100%"
            />
          </Carousel.Slide>
        );
      }) || [],
    [causes, navigate]
  );

  return (
    <Stack p="md">
      {causes?.length == 0 ? (
        <Text fz="14px" fw={600} c="gray.6">
          No orgs near you
        </Text>
      ) : (
        <Carousel
          // Use hardcoded because CarouselMantine is based on the Mantine breakpoints but those are not supported when using
          // type='container'
          slideSize={{ base: '50%', '47.9375em': '33%' }}
          slideGap="md"
          align={'start'}
          type="container"
          withControls={!isSmallScreen}
          withIndicators
          nextControlIcon={
            <ThemeIcon c="gray" variant="transparent">
              <IconChevronRight />
            </ThemeIcon>
          }
          previousControlIcon={
            <ThemeIcon c="gray" variant="transparent">
              <IconChevronLeft />
            </ThemeIcon>
          }
          classNames={classes}
        >
          {causeSlides}
        </Carousel>
      )}
    </Stack>
  );
};

const CausesNearYou = () => {
  const { status } = useUserLocation({
    showPromptNotification: false,
  });

  return (
    <Stack>
      <Text fz="18px" fw={600}>
        Near You
      </Text>
      {status != 'granted' ? <LocationExploreCard /> : <CausesSlides />}
    </Stack>
  );
};

export { CausesNearYou };
